import React, { useState, useEffect } from 'react';
import { getDispatchSuggestions, analyzeFleetEfficiency, isAIConfigured } from '../services/aiDispatchService';
import { supabase } from '../supabaseClient';
import { useEquipmentGate } from './EquipmentGate';   // __EQUIP_COMPAT__

function AIDispatchSuggestions({ load, onAssign, onClose }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [method, setMethod] = useState('');
  const [aiNote, setAiNote] = useState('');
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [gateEquipment, equipmentModal] = useEquipmentGate();

  useEffect(() => {
    fetchSuggestions();
  }, [load]);

  const fetchSuggestions = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch drivers
      const { data: drivers, error: driversError } = await supabase
        .from('drivers')
        .select('*');
      
      if (driversError) throw driversError;

      // Fetch trucks
      const { data: trucks, error: trucksError } = await supabase
        .from('trucks')
        .select('*');
      
      if (trucksError) throw trucksError;

      // Fetch all loads for workload analysis
      const { data: allLoads, error: loadsError } = await supabase
        .from('loads')
        .select('id, status, driver_id, truck_id');
      
      if (loadsError) throw loadsError;

      // Get suggestions
      const result = await getDispatchSuggestions(load, drivers || [], trucks || [], allLoads || []);
      
      setSuggestions(result.suggestions || []);
      setMethod(result.method);
      setAiNote(result.aiNote || result.aiError || '');
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (suggestion) => {
    if (!suggestion.driver || !suggestion.truck) {
      setError('Invalid suggestion - missing driver or truck');
      return;
    }

    setAssigning(true);
    setSelectedSuggestion(suggestion);

    try {
      // __EQUIP_COMPAT__ the suggested driver + truck vs this job, before anything is written
      const eq = await gateEquipment({ driverId: suggestion.driver.id, truckId: suggestion.truck.id, loads: [load], context: 'ai_suggestion', title: 'AI suggestion' });
      if (!eq.proceed) return;
      const { error } = await supabase
        .from('loads')
        .update({
          driver_id: suggestion.driver.id,
          truck_id: suggestion.truck.id,
          status: 'assigned'
        })
        .eq('id', load.id);

      if (error) throw error;

      if (onAssign) {
        onAssign(suggestion);
      }
      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error('Error assigning load:', err);
      setError('Failed to assign: ' + err.message);
    } finally {
      setAssigning(false);
      setSelectedSuggestion(null);
    }
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high': return 'var(--color-success)';
      case 'medium': return 'var(--color-warning)';
      case 'low': return 'var(--color-danger)';
      default: return 'var(--text-muted)';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'var(--color-success)';
    if (score >= 50) return 'var(--color-warning)';
    return 'var(--color-danger)';
  };

  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  };

  const contentStyle = {
    backgroundColor: 'var(--surface-1)',
    borderRadius: '10px',
    width: '90%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflow: 'auto',
    padding: '2rem'
  };

  return (
    <>
    <div style={modalStyle} onClick={onClose}>
      <div style={contentStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h2 style={{ margin: 0, color: 'var(--accent-primary)' }}>
              🤖 AI Dispatch Suggestions
            </h2>
            <p style={{ margin: '0.5rem 0 0 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              Load #{load.load_number} • {load.customer?.company_name || 'Unknown Customer'}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-primary)',
              fontSize: '1.5rem',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>

        {/* Method Badge */}
        <div style={{ marginBottom: '1rem' }}>
          <span style={{
            padding: '0.25rem 0.75rem',
            borderRadius: '15px',
            fontSize: '0.8rem',
            backgroundColor: method === 'ai-powered' ? 'var(--color-info)' : 'var(--accent-blue)',
            color: 'var(--text-primary)'
          }}>
            {method === 'ai-powered' ? '🧠 AI-Powered' : '📊 Rule-Based'}
          </span>
          {!isAIConfigured() && (
            <span style={{
              marginLeft: '0.5rem',
              padding: '0.25rem 0.75rem',
              borderRadius: '15px',
              fontSize: '0.8rem',
              backgroundColor: 'var(--color-warning)',
              color: 'var(--text-primary)'
            }}>
              Configure AI in Settings for smarter suggestions
            </span>
          )}
        </div>

        {/* AI Note */}
        {aiNote && (
          <div style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--surface-2)',
            borderRadius: '5px',
            marginBottom: '1rem',
            color: 'var(--color-warning)',
            fontSize: '0.9rem'
          }}>
            {aiNote}
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--color-danger)',
            borderRadius: '5px',
            marginBottom: '1rem',
            color: 'var(--text-primary)'
          }}>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--accent-primary)' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔄</div>
            Analyzing dispatch options...
          </div>
        )}

        {/* Suggestions */}
        {!loading && suggestions.length > 0 && (
          <div>
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: 'var(--surface-2)',
                  borderRadius: '8px',
                  padding: '1.25rem',
                  marginBottom: '1rem',
                  border: suggestion.rank === 1 ? '2px solid var(--accent-primary)' : '1px solid var(--border-default)'
                }}
              >
                {/* Rank and Score */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{
                      width: '30px',
                      height: '30px',
                      borderRadius: '50%',
                      backgroundColor: suggestion.rank === 1 ? 'var(--accent-primary)' : 'var(--border-default)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }}>
                      #{suggestion.rank}
                    </span>
                    {suggestion.rank === 1 && (
                      <span style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>Recommended</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '5px',
                      fontSize: '0.8rem',
                      backgroundColor: getConfidenceColor(suggestion.confidence),
                      color: 'var(--text-primary)'
                    }}>
                      {suggestion.confidence} confidence
                    </span>
                    <span style={{
                      fontSize: '1.25rem',
                      fontWeight: 'bold',
                      color: getScoreColor(suggestion.overallScore)
                    }}>
                      {suggestion.overallScore}%
                    </span>
                  </div>
                </div>

                {/* Driver Info */}
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ flex: 1, backgroundColor: 'var(--bg-primary)', padding: '0.75rem', borderRadius: '5px' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Driver</div>
                    <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>
                      {suggestion.driver?.name || suggestion.driver?.user_id?.name || `Driver #${suggestion.driver?.id}`}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                      {suggestion.driver?.phone || 'No phone'}
                    </div>
                    <div style={{ marginTop: '0.5rem' }}>
                      {suggestion.driverReasons?.map((reason, i) => (
                        <span key={i} style={{
                          display: 'inline-block',
                          padding: '0.15rem 0.5rem',
                          backgroundColor: 'var(--surface-2)',
                          borderRadius: '3px',
                          fontSize: '0.75rem',
                          marginRight: '0.25rem',
                          marginBottom: '0.25rem',
                          color: 'var(--accent-primary)'
                        }}>
                          {reason}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div style={{ flex: 1, backgroundColor: 'var(--bg-primary)', padding: '0.75rem', borderRadius: '5px' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Truck</div>
                    <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>
                      {suggestion.truck?.truck_number || `Truck #${suggestion.truck?.id}`}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                      {suggestion.truck?.license_plate || 'No plate'}
                    </div>
                    <div style={{ marginTop: '0.5rem' }}>
                      {suggestion.truckReasons?.map((reason, i) => (
                        <span key={i} style={{
                          display: 'inline-block',
                          padding: '0.15rem 0.5rem',
                          backgroundColor: 'var(--surface-2)',
                          borderRadius: '3px',
                          fontSize: '0.75rem',
                          marginRight: '0.25rem',
                          marginBottom: '0.25rem',
                          color: 'var(--color-warning)'
                        }}>
                          {reason}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* AI Reasoning */}
                {suggestion.aiReasoning && (
                  <div style={{
                    padding: '0.5rem 0.75rem',
                    backgroundColor: 'var(--bg-primary)',
                    borderRadius: '5px',
                    marginBottom: '1rem',
                    fontSize: '0.85rem',
                    color: 'var(--text-muted)',
                    fontStyle: 'italic'
                  }}>
                    💡 {suggestion.aiReasoning}
                  </div>
                )}

                {/* Assign Button */}
                <button
                  onClick={() => handleAssign(suggestion)}
                  disabled={assigning}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: assigning && selectedSuggestion === suggestion ? 'var(--surface-3)' : 'var(--accent-primary)',
                    color: 'var(--text-primary)',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: assigning ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold',
                    fontSize: '1rem'
                  }}
                >
                  {assigning && selectedSuggestion === suggestion ? 'Assigning...' : 'Assign This Combination'}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* No Suggestions */}
        {!loading && suggestions.length === 0 && !error && (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🤷</div>
            No dispatch suggestions available. Make sure you have drivers and trucks in the system.
          </div>
        )}

        {/* Refresh Button */}
        {!loading && (
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <button
              onClick={fetchSuggestions}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'var(--accent-blue)',
                color: 'var(--text-primary)',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              🔄 Refresh Suggestions
            </button>
          </div>
        )}
      </div>
    </div>
    {equipmentModal}
    </>
  );
}

export default AIDispatchSuggestions;
